import React, { memo } from 'react'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Box } from 'transfix-ui/core'
import { IDocument, INote } from 'types'
import { attachMultipleFilesToNote, editNote } from 'apps/marketplace/utils/api/notes'
import { Document } from 'components/atoms'
import { Checkbox } from 'components/forms'
import { parseNoteTags } from 'utils/parseNoteTags'
import FileUpload from './form-components/FileUpload'

interface INoteProps {
  note: INote
  updateNote: (noteId: string, note: INote) => void
}

/** Each Note component displayed in a list */
const Note = memo<INoteProps>(({ note, updateNote }) => {
  const fileUploadHandler = (attachments: IDocument[]) => {
    const documents = attachments.map(doc => {
      return {
        ...doc,
        documentUrl: doc.fileUrl,
        documentName: doc.fileName,
      }
    })

    updateNote(note.id, {
      ...note,
      documents,
    })
  }

  const submitNote = (fileList: File[]) => attachMultipleFilesToNote(note.id, fileList)

  const handleCallbackCheck = () =>
    editNote({
      ...note,
      calledBackAt: true,
      needsCallback: note.callBackAt,
    }).then(resp => updateNote(note.id, { ...note, ...resp }))

  return (
    <div className='note-item mb5 pt0' key={note.id}>
      <div className='flex note-item--header'>
        <div className='b mr2'>{note.creatorName} </div>
        <div className='text-secondary'>
          <FormattedDateTime
            datetime={note.createdAt}
            displayTimezone='America/New_York'
            format='timeWeekdayDateAndYear'
          />
        </div>
        <Box ml={1}>
          <FileUpload noteId={note.id} onUpload={fileUploadHandler} submitNoteApi={submitNote} />
        </Box>
      </div>
      {/* Parsed note content */}
      <div
        className='text-default'
        // Display notes parsed with additional styling and html elements applied
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: parseNoteTags(note) }}
      />
      {/* All attached documents  */}
      {note.documents.map(document => {
        const { documentName, documentUrl } = document
        return (
          // Note Documents
          <div key={documentName} className='flex items-center'>
            <Document className='inline large db icon-spruce-green' />
            <div className='ml1'>
              <a href={documentUrl} target='_blank' rel='noopener noreferrer'>
                {documentName}
              </a>
            </div>
          </div>
        )
      })}
      {/* Callback indicator */}
      {note.callBackAt && (
        <div className='flex'>
          <small className={`mr2 ${note.calledBackAt ? 'strike' : ''}`}>
            Callback:{' '}
            <FormattedDateTime
              datetime={note.callBackAt}
              displayTimezone='America/New_York'
              format='timeWeekdayDateAndYear'
            />
          </small>
          {!note.calledBackAt && <Checkbox onChange={handleCallbackCheck} />}
        </div>
      )}
    </div>
  )
})

export default Note
